import { AvatarAnimation } from "../types/animation";
import {
  DerivedStats,
  EconomyContext,
  GameState,
  RuntimeContext,
} from "../types/game";
import { PowerupDefinition, PowerupEffect } from "../types/powerups";
import { UnlockRequirement, UnlockableAnimation } from "../types/unlock";
import { createDefaultAvatarState } from "./avatar";

const BASE_CHAT_RATE_PER_VIEWER = 0.0025;
const BASE_BAD_COMMENT_RATE_PER_VIEWER = 0.0007;
const BASE_REAL_VIEWER_DECAY_PER_SECOND = 0.12;

export const getPowerupCost = (
  def: PowerupDefinition,
  level: number,
): number => {
  const scaling = def.scaling;

  switch (scaling.method) {
    case "flat":
      return Math.floor(scaling.baseCost);

    case "linear":
      return Math.floor(scaling.baseCost + (scaling.step ?? 0) * level);

    case "exponential":
      return Math.floor(
        scaling.baseCost * Math.pow(scaling.factor ?? 1.15, level),
      );

    case "tiered": {
      if (scaling.tiers.length === 0) {
        return Math.floor(scaling.baseCost);
      }

      let activeTier = scaling.tiers[0];

      for (const tier of scaling.tiers) {
        if (level >= tier.startLevel) {
          activeTier = tier;
        } else {
          break;
        }
      }

      const levelInTier = level - activeTier.startLevel;
      const tierBaseCost = activeTier.baseCost ?? scaling.baseCost;

      if (activeTier.step != null) {
        return Math.floor(tierBaseCost + activeTier.step * levelInTier);
      }

      return Math.floor(
        tierBaseCost * Math.pow(activeTier.factor ?? 1.15, levelInTier),
      );
    }
  }
};

const multiplyAnimationPower = (
  runtime: RuntimeContext,
  animationId: UnlockableAnimation,
  multiplier: number,
) => {
  const avatarAnimation = animationId as AvatarAnimation;
  runtime.animationPowerMultiplierById[avatarAnimation] =
    (runtime.animationPowerMultiplierById[avatarAnimation] ?? 1) * multiplier;
};

const multiplyAnimationCooldown = (
  runtime: RuntimeContext,
  animationId: UnlockableAnimation,
  multiplier: number,
) => {
  const avatarAnimation = animationId as AvatarAnimation;
  runtime.animationCooldownMultiplierById[avatarAnimation] =
    (runtime.animationCooldownMultiplierById[avatarAnimation] ?? 1) *
    multiplier;
};

const enableChat = (econ: EconomyContext, runtime: RuntimeContext) => {
  runtime.chatUnlocked = true;
  econ.chatRatePerViewer = Math.max(
    econ.chatRatePerViewer,
    BASE_CHAT_RATE_PER_VIEWER,
  );
  econ.badCommentRatePerViewer = Math.max(
    econ.badCommentRatePerViewer,
    BASE_BAD_COMMENT_RATE_PER_VIEWER,
  );
};

const applyPowerupEffect = (
  effect: PowerupEffect,
  level: number,
  econ: EconomyContext,
  runtime: RuntimeContext,
) => {
  switch (effect.type) {
    case "add_economy": {
      if (effect.target === "bottedViewers") {
        return;
      }

      econ[effect.target] += effect.amount * level;
      return;
    }

    case "multiply_economy": {
      econ[effect.target] *= Math.max(0, 1 + effect.amount * level);
      return;
    }

    case "enable_chat": {
      if (level > 0) {
        enableChat(econ, runtime);
      }
      return;
    }

    case "unlock_animation": {
      runtime.unlockedAnimations[effect.animationId as AvatarAnimation] =
        level > 0;
      return;
    }

    case "multiply_animation_power": {
      multiplyAnimationPower(
        runtime,
        effect.animationId,
        1 + effect.amount * level,
      );
      return;
    }

    case "multiply_animation_cooldown": {
      const minMultiplier = effect.minMultiplier ?? 0;
      multiplyAnimationCooldown(
        runtime,
        effect.animationId,
        Math.max(minMultiplier, 1 - effect.amount * level),
      );
      return;
    }

    case "automate_animation": {
      runtime.automationById[effect.animationId as AvatarAnimation] = level > 0;
      return;
    }

    case "unlock_clothing": {
      runtime.unlockedClothing[effect.clothingId] = level > 0;
      return;
    }

    case "multiply_unlocked_animation_power": {
      for (const animationId of Object.keys(
        runtime.unlockedAnimations,
      ) as UnlockableAnimation[]) {
        if (!runtime.unlockedAnimations[animationId]) continue;
        multiplyAnimationPower(runtime, animationId, 1 + effect.amount * level);
      }
      return;
    }
  }
};

const isDeferredEffect = (effect: PowerupEffect): boolean =>
  effect.type === "multiply_unlocked_animation_power";

const applyPersistentPowerupEffectToState = (
  state: GameState,
  effect: PowerupEffect,
): GameState => {
  switch (effect.type) {
    case "enable_chat": {
      return {
        ...state,
        unlocks: {
          ...state.unlocks,
          chat: true,
        },
      };
    }

    case "unlock_animation": {
      return {
        ...state,
        unlocks: {
          ...state.unlocks,
          animations: {
            ...state.unlocks.animations,
            [effect.animationId]: true,
          },
        },
      };
    }

    case "unlock_clothing": {
      return {
        ...state,
        unlocks: {
          ...state.unlocks,
          clothing: {
            ...state.unlocks.clothing,
            [effect.clothingId]: true,
          },
        },
      };
    }

    case "unlock_stream_title": {
      return {
        ...state,
        streamTitle: {
          unlocked: true,
          value:
            state.streamTitle.value.trim().length > 0
              ? state.streamTitle.value
              : "Late Night Stream",
        },
      };
    }

    case "unlock_music": {
      return {
        ...state,
        unlocks: {
          ...state.unlocks,
          music: {
            ...state.unlocks.music,
            [effect.musicId]: true,
          },
        },
      };
    }

    case "set_avatar_slots": {
      return {
        ...state,
        avatarState: {
          ...state.avatarState,
          slots: {
            ...state.avatarState.slots,
            ...effect.slots,
          },
        },
      };
    }

    default:
      return state;
  }
};

export const applyPowerupPurchaseToState = (
  state: GameState,
  def: PowerupDefinition,
): GameState => {
  let nextState = state;

  for (const effect of def.effects) {
    switch (effect.type) {
      case "add_economy": {
        if (effect.target !== "bottedViewers") {
          continue;
        }

        nextState = {
          ...nextState,
          viewers: {
            ...nextState.viewers,
            botted: nextState.viewers.botted + effect.amount,
          },
        };
        continue;
      }

      case "add_real_viewers": {
        nextState = {
          ...nextState,
          viewers: {
            ...nextState.viewers,
            real: nextState.viewers.real + effect.amount,
          },
        };
        continue;
      }

      default: {
        nextState = applyPersistentPowerupEffectToState(nextState, effect);
        continue;
      }
    }
  }

  return nextState;
};

export const reconcileOwnedPowerupState = (
  state: GameState,
  defs: readonly PowerupDefinition[],
): GameState => {
  let nextState: GameState = {
    ...state,
    avatarState: {
      ...state.avatarState,
      slots: {
        ...createDefaultAvatarState().slots,
      },
    },
    unlocks: {
      ...state.unlocks,
      chat: state.unlocks.chat,
      animations: {
        ...state.unlocks.animations,
      },
      clothing: {
        ...state.unlocks.clothing,
      },
      music: {
        ...state.unlocks.music,
      },
    },
  };

  for (const def of defs) {
    const level = state.powerups[def.id] ?? 0;
    if (level <= 0) continue;

    for (const effect of def.effects) {
      nextState = applyPersistentPowerupEffectToState(nextState, effect);
    }
  }

  return nextState;
};

export const createBaseEconomyContext = (): EconomyContext => {
  return {
    likesPerClick: 1,
    likesPerSecondMultiplier: 1,
    likesPerViewerPerSecond: 0.14,
    realViewerFlowPerSecond: 0,
    realViewerRetention: 0,
    realViewers: 0,
    bottedViewers: 0,
    chatRatePerViewer: 0,
    badCommentRatePerViewer: 0,
    giftRatePerViewer: 0.004,
  };
};

export const createBaseRuntimeContext = (state: GameState): RuntimeContext => {
  return {
    chatUnlocked: state.unlocks.chat,
    unlockedAnimations: { ...state.unlocks.animations },
    unlockedClothing: { ...state.unlocks.clothing },
    animationCooldownMultiplierById: {},
    animationPowerMultiplierById: {},
    automationById: {},
  };
};

export const applyPowerupToContexts = (
  def: PowerupDefinition,
  level: number,
  econ: EconomyContext,
  runtime: RuntimeContext,
): void => {
  for (const effect of def.effects) {
    if (isDeferredEffect(effect)) continue;
    applyPowerupEffect(effect, level, econ, runtime);
  }
};

const applyDeferredPowerupEffects = (
  def: PowerupDefinition,
  level: number,
  econ: EconomyContext,
  runtime: RuntimeContext,
) => {
  for (const effect of def.effects) {
    if (!isDeferredEffect(effect)) continue;
    applyPowerupEffect(effect, level, econ, runtime);
  }
};

export const computeDerivedStats = (
  state: GameState,
  defs: readonly PowerupDefinition[],
): DerivedStats => {
  const econ = createBaseEconomyContext();
  const runtime = createBaseRuntimeContext(state);

  econ.realViewers = state.viewers.real;
  econ.bottedViewers = state.viewers.botted;

  if (runtime.chatUnlocked) {
    enableChat(econ, runtime);
  }

  for (const def of defs) {
    const level = state.powerups[def.id] ?? 0;
    if (level <= 0) continue;

    applyPowerupToContexts(def, level, econ, runtime);
  }

  for (const def of defs) {
    const level = state.powerups[def.id] ?? 0;
    if (level <= 0) continue;

    applyDeferredPowerupEffects(def, level, econ, runtime);
  }

  const totalViewers = econ.realViewers + econ.bottedViewers;
  const realViewerDecayPerSecond =
    BASE_REAL_VIEWER_DECAY_PER_SECOND *
    Math.max(0.08, 1 - econ.realViewerRetention);

  return {
    likesPerClick: econ.likesPerClick,
    likesPerSecond:
      totalViewers *
      econ.likesPerViewerPerSecond *
      econ.likesPerSecondMultiplier,
    totalViewers,
    realViewers: econ.realViewers,
    bottedViewers: econ.bottedViewers,
    realViewerFlowPerSecond: econ.realViewerFlowPerSecond,
    realViewerRetention: econ.realViewerRetention,
    chatEventsPerSecond: totalViewers * econ.chatRatePerViewer,
    badCommentsPerSecond: totalViewers * econ.badCommentRatePerViewer,
    giftsPerSecond: totalViewers * econ.giftRatePerViewer,
    animationCooldownMultiplierById: runtime.animationCooldownMultiplierById,
    animationPowerMultiplierById: runtime.animationPowerMultiplierById,
    unlockedAnimations: runtime.unlockedAnimations,
    unlockedClothing: runtime.unlockedClothing,
    automationById: runtime.automationById,
    realViewerDecayPerSecond,
  };
};

const isChatUnlocked = (
  state: GameState,
): boolean => {
  return state.unlocks.chat;
};

const meetsUnlockRequirement = (
  state: GameState,
  requirement: UnlockRequirement,
): boolean => {
  switch (requirement.type) {
    case "likes":
      return state.likes >= requirement.amount;

    case "powerup_level":
      return (state.powerups[requirement.powerupId] ?? 0) >= requirement.level;

    case "chat_unlocked":
      return isChatUnlocked(state);

    case "animation_unlocked":
      return state.unlocks.animations[requirement.animationId] ?? false;
  }
};

export const isPowerupUnlocked = (
  state: GameState,
  def: PowerupDefinition,
): boolean => {
  return (def.unlockRequirements ?? []).every((requirement) =>
    meetsUnlockRequirement(state, requirement),
  );
};

export const canPurchasePowerup = (
  state: GameState,
  def: PowerupDefinition,
): boolean => {
  const level = state.powerups[def.id] ?? 0;

  if (def.maxLevel != null && level >= def.maxLevel) {
    return false;
  }

  if (!isPowerupUnlocked(state, def)) {
    return false;
  }

  const cost = getPowerupCost(def, level);
  return state.likes >= cost;
};
